import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { Collaborator } from '../shared/CollaboratorSelector';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { createProject } from '../../store/actions/Projects/ProjectActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';

interface CreateProjectFormProps {
  onClose: (newUuid?: string) => void;
}

const CreateProjectForm: React.FC<CreateProjectFormProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Load org people for collaborator selector
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingData(true);
      try {
        await dispatch(fetchOrgPeople());
      } catch (error: any) {
        // Non-critical - form still works without collaborator list
      } finally {
        setIsLoadingData(false);
      }
    };
    loadData();
  }, [dispatch]);

  // ─── Form sections ─────────────────────────────────────────────

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    return [
      {
        title: t('Project Details'),
        fields: [
          {
            name: 'title',
            type: 'input',
            label: t('Project Name'),
            placeholder: t('e.g., Smith Acquisition Due Diligence'),
            required: true,
            gridSize: 'full',
          } as BaseFormFieldConfig,
          {
            name: 'description',
            type: 'textarea',
            label: t('Description'),
            placeholder: t('Briefly describe this project...'),
            rows: 3,
            gridSize: 'full',
          } as BaseFormFieldConfig,
          {
            name: 'status',
            type: 'dropdown',
            label: t('Status'),
            options: [
              { value: 'active', label: t('Active') },
              { value: 'archived', label: t('Archived') },
              { value: 'completed', label: t('Completed') },
            ],
            gridSize: 'half',
            customProps: { fullWidth: true },
          } as BaseFormFieldConfig,
          {
            name: 'priority',
            type: 'dropdown',
            label: t('Priority'),
            placeholder: t('Select priority'),
            options: [
              { value: '', label: t('None') },
              { value: 'low', label: t('Low') },
              { value: 'medium', label: t('Medium') },
              { value: 'high', label: t('High') },
              { value: 'urgent', label: t('Urgent') },
            ],
            gridSize: 'half',
            customProps: { fullWidth: true },
          } as BaseFormFieldConfig,
          {
            name: 'due_date',
            type: 'datePicker',
            label: t('Due Date'),
            gridSize: 'full',
            customProps: { fullWidth: true },
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Access'),
        description: t('Add team members who should have access to this project.'),
        fields: [
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: t('Access'),
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
    ];
  }, [t]);

  // ─── Validation schema ─────────────────────────────────────────

  const schema = useMemo(() => {
    return new FormSchema('createProject', [
      new FormField('title', 'text', true, t('Project Name'), { maxLength: 255 }),
      new FormField('description', 'text', false, t('Description'), { maxLength: 5000 }),
      new FormField('status', 'text', false, t('Status')),
      new FormField('priority', 'text', false, t('Priority')),
      new FormField('due_date', 'text', false, t('Due Date')),
      new FormField('collaborators', 'text', false, t('Access')),
    ]);
  }, [t]);

  // ─── Submit ────────────────────────────────────────────────────

  const handleFormSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsSubmitting(true);
      try {
        const payload: any = {
          title: String(formData.title).trim(),
        };

        if (formData.description?.trim()) {
          payload.description = formData.description.trim();
        }
        if (formData.status) {
          payload.status = formData.status;
        }
        if (formData.priority) {
          payload.priority = formData.priority;
        }
        if (formData.due_date) {
          payload.due_date = formData.due_date instanceof Date
            ? formData.due_date.toISOString()
            : formData.due_date;
        }
        if (formData.collaborators && formData.collaborators.length > 0) {
          payload.shared_with = formData.collaborators.map((c: Collaborator) => ({
            id: c.user_id,
            collaborator_uuid: c.collaborator_uuid,
            access: c.access || 'view',
          }));
        }

        const result = await dispatch(createProject(payload));
        if (result) {
          onClose(result.uuid);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, onClose]
  );

  // ─── Render ────────────────────────────────────────────────────

  return (
    <BaseForm
      title={t('Create Project')}
      description={t('Projects let you group matters, tasks, files, and other resources together.')}
      sections={formSections}
      schema={schema}
      initialValues={{ status: 'active' }}
      onSubmit={handleFormSubmit}
      onCancel={() => onClose()}
      submitLabel={t('Create Project')}
      cancelLabel={t('Cancel')}
      isSubmitting={isSubmitting}
    />
  );
};

export default CreateProjectForm;
